import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, take } from 'rxjs/operators';
import { BoudActionTypes, CreateBoudAction, DeleteBoudAction, EditBoudAction, GeneratePdfBoudAction, PublishBoudAction, ValidateBoudAction, ViewBoudAction } from '../actions/boud-actions';
import { BoudService } from '../../services/boud.service';
import { AppStore, getAppSelection, SnackbarErrorAction, SnackbarInfoAction } from '@alfresco/aca-shared/store';
import { UdeuService } from '../../services/udeu.service';
import { Store } from '@ngrx/store';
import { UdeuStatus } from '../../model/classes/udeu-status';
import { UDEU_BOUD_VALIDATED_STATUS } from '../../model/contants/deusto';
import { SMTConfirmDialogComponent } from 'projects/alden-extension/src/lib/dialogs/confirm/confirm.dialog';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslationService } from '@alfresco/adf-core';
import { UdeuStatusMultiple } from '../../model/classes/udeu-status-multiple';
import { SMTLoadingDialogComponent } from 'projects/alden-extension/src/lib/dialogs/loading/loading.dialog';
import { RecordService } from '../../services/record.service';
import { RecordActionTypes } from '../actions/record-actions';

@Injectable()
export class BoudEffects {
  constructor(
    private actions$: Actions,
    private boudService: BoudService,
    private recordService: RecordService,
    private udeuService: UdeuService,
    private store: Store<AppStore>,
    private dialog: MatDialog,
    private translationService: TranslationService
  ) { }


  createBoud$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CreateBoudAction>(BoudActionTypes.CreateBoud),
        map((_action) => {
          this.boudService.openBoudDialogComponent();
        })
      ),
    { dispatch: false }
  );

  viewBoud$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ViewBoudAction>(BoudActionTypes.ViewBoud),
        map((action) => {
          this.openBoudDialogComponent(action, BoudActionTypes.ViewBoud);
        })
      ),
    { dispatch: false }
  );

  editBoud$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<EditBoudAction>(BoudActionTypes.EditBoud),
        map((action) => {
          this.openBoudDialogComponent(action, BoudActionTypes.EditBoud);
        })
      ),
    { dispatch: false }
  );

  validateBoud$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ValidateBoudAction>(BoudActionTypes.ValidateBoud),
        map((action: any) => {
          if (action && action.payload && action.payload.length > 0) {
            const UUIDs = [action];
            this.validateBoud(UUIDs);
          } else {
            this.store
              .select(getAppSelection)
              .pipe(take(1))
              .subscribe((selection) => {
                if (selection && selection.count > 0) {
                  const UUIDs = selection.nodes.map((nodeEntry) => nodeEntry.entry.id);
                  this.validateBoud(UUIDs);
                }
              });
          }
        })
      ),
    { dispatch: false }
  );

  generatePdfBoud$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<GeneratePdfBoudAction>(BoudActionTypes.GeneratePdfBoud),
        map((action: any) => {
          this.publishBoud(action, true);
        })
      ),
    { dispatch: false }
  );

  publishBoud$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<PublishBoudAction>(BoudActionTypes.PublishBoud),
        map((action) => {
          this.publishBoud(action);
        })
      ),
    { dispatch: false }
  );

  deleteBoud$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<DeleteBoudAction>(BoudActionTypes.DeleteBoud),
        map((action) => {
          if (action && action.payload && action.payload.length > 0) {
            this.udeuService.deleteNode(action.payload);
          } else {
            this.store
              .select(getAppSelection)
              .pipe(take(1))
              .subscribe((selection) => {
                if (selection && selection.count > 0) {
                  this.udeuService.deleteNode(selection.nodes);
                }
              });
          }
        })
      ),
    { dispatch: false }
  );

  private openBoudDialogComponent(action: any, boudActionType: BoudActionTypes) {
      if (action && action.payload && action.payload.length > 0) {
        this.boudService.openBoudDialogComponent(action, boudActionType);
      } else {
        this.store
          .select(getAppSelection)
          .pipe(take(1))
          .subscribe((selection) => {
            if (selection && selection.count > 0) {
              const UUID = selection.nodes.map((nodeEntry) => nodeEntry.entry.id)[0];
              this.boudService.openBoudDialogComponent(UUID, boudActionType);
            }
          });
      }
  }

  private validateBoud(UUIDs: string[]) {
    const dialogRef = this.dialog.open(SMTConfirmDialogComponent, {
      data: {
        title: UUIDs.length === 1 ? 'DEUSTO.DIALOGS.VALIDATE_BOUD.ELEMENT_TITLE' : 'DEUSTO.DIALOGS.VALIDATE_BOUD.TITLE',
        htmlContent: this.translationService.instant(UUIDs.length === 1 ? 'DEUSTO.DIALOGS.VALIDATE_BOUD.ELEMENT_MESSAGE' : 'DEUSTO.DIALOGS.VALIDATE_BOUD.MESSAGE', { number: UUIDs.length }),
        noLabel: 'DEUSTO.DIALOGS.VALIDATE_BOUD.NO_LABEL',
        yesLabel: 'DEUSTO.DIALOGS.VALIDATE_BOUD.YES_LABEL',
      },
      minWidth: '250px'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const statusMultiple = new UdeuStatusMultiple(UUIDs, new UdeuStatus(UDEU_BOUD_VALIDATED_STATUS));
        this.udeuService.updateStatusMultiple(statusMultiple).then(() => {
          this.udeuService.reloadCustomListTable();
          this.store.dispatch(new SnackbarInfoAction(UUIDs.length === 1 ? 'Elemento BOUD validado correctamente.' : 'Elementos BOUD validados correctamente.'));
        }).catch((err) => { 
          console.error("ERROR", err)
          this.store.dispatch(new SnackbarErrorAction('ALDEN.MESSAGES.ERRORS.RESPONSE.500'));
        });
      }
    });
  }

  private publishBoud(action: any, individual?: boolean) {
    const dialogRef = this.dialog.open(SMTLoadingDialogComponent, {
      data: {
        title: 'Cargando...',
      },
      minWidth: '250px'
    });

    if (action && action.payload && action.payload.length > 0) {
      const UUIDs = [action];
      const promise = individual ? this.boudService.generateBoud(UUIDs[0]) : this.boudService.publishBoud(UUIDs);
      this.resolvePublishBoudRes(promise, dialogRef, individual, individual ? UUIDs[0] : null);
    } else {
      this.store
        .select(getAppSelection)
        .pipe(take(1))
        .subscribe((selection) => {
          if (selection && selection.count > 0) {
            const UUIDs = selection.nodes.map((nodeEntry) => nodeEntry.entry.id);
            const promise = individual ? this.boudService.generateBoud(UUIDs[0]) : this.boudService.publishBoud(UUIDs);
            this.resolvePublishBoudRes(promise, dialogRef, individual, individual ? UUIDs[0] : null);
          }
        });
    }
  }

  private resolvePublishBoudRes(promise: Promise<any>, dialogRef: MatDialogRef<SMTLoadingDialogComponent, any>, individual?: boolean, individualElementId?: string) {
    promise.then((res: any) => {
      if(individual) {
        // Al abrir más de uno el navegador bloquea los siguientes
        this.boudService.openBoudDialogComponent(individualElementId, BoudActionTypes.ViewBoud, 2);
      } else {
        // Abrimos el registro
        const recordId = res as string;
        this.udeuService.reloadCustomListTable();
        this.recordService.openRecordDialogComponent([recordId], RecordActionTypes.ViewRecord, false, 5);
      }
    }).catch((err) => { 
      console.error("ERROR", err)
      this.store.dispatch(new SnackbarErrorAction('ALDEN.MESSAGES.ERRORS.RESPONSE.500'));
    }).finally(() => dialogRef.close() );
  }

}
